import { DateTime } from '@ksyos/date-time';
import { Form, FormGroup, TextInput } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { useEpisodeContext } from '@frontend/utils/hooks/useEpisodeContext';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getSelectedLanguage } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer } from '../../../../elements/common';
import { IndependentDateInput } from '../../../../fields/Date';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { OsasqConfirmationModal } from './OsasqConfirmationModal';

interface ICustomControlDataOsasq {
    email_address: string;
    resend_date: Date;
}

export function OsasqV1RequestAddEdit(props: Readonly<ICustomControlRequestAddEditProps>) {
    const translate = getSelectedLanguage();
    const { patient } = useEpisodeContext();

    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);

    useEffect(() => {
        props.handleChangeCustomControl({ email_address: patient.emailAddress });
    }, []);

    return (
        <NewOrEditRequestProcessContainer
            isExaminationRequest
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            <Form submitOnEnter={false}>
                <FormGroup
                    hasErrors={props.errors.has('customControlData.email_address')}
                    isRequired
                    label={translate.common.emailAddress}
                >
                    <TextInput
                        error={getValidationErrorTranslation(props.errors.get('customControlData.email_address'))}
                        onBlur={async () => await props.validateOneCustomControl('email_address')}
                        onChange={async ({ target: { value } }) =>
                            await props.handleChangeCustomControl({ email_address: value })
                        }
                        placeholder={translate.common.emailAddress}
                        value={props.request.customControlData.email_address ?? ''}
                        variant="outline"
                    />
                </FormGroup>

                <FormGroup
                    hasErrors={props.errors.has('customControlData.resend_date')}
                    isRequired
                    label={translate.processThirdPartyMessages.nextScheduledDateTime}
                >
                    <IndependentDateInput
                        error={props.errors.get('customControlData.resend_date')}
                        hasMaxWidth={true}
                        minDate={DateTime.now().format()}
                        onChange={async (value) => {
                            await props.handleChangeCustomControl({ resend_date: value });
                            await props.validateOneCustomControl('resend_date');
                        }}
                        placeholder={translate.common.datePlaceholder}
                        value={props.request.customControlData.resend_date?.toString() ?? ''}
                        variant="outline"
                    />
                </FormGroup>

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!props.request.id}
                            key="cancel"
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveSendButton
                            buttonDisabled={isButtonDisabled}
                            key="saveSend"
                            saveSend={async () => await saveRequest()}
                        />,
                    ]}
                />

                <OsasqConfirmationModal
                    patientId={props.request.patientId}
                    serviceId={props.request.serviceId}
                />
            </Form>
        </NewOrEditRequestProcessContainer>
    );

    async function saveRequest() {
        setIsButtonDisabled(true);

        const validatedRequest = await props.validateRequest('final');
        if (!validatedRequest) {
            setIsButtonDisabled(false);

            return;
        }
        const requestId = await props.onSaveRequest('final');
        showServiceToast(
            ToastType.success,
            'toast.questionnaireScheduled',
            props.service,
            'request',
            translate.toast.submitDefaultMessage
        );

        await props.onSave(requestId);
    }
}

export function osasqV1RequestValidateOneCustomControlData(
    name: string,
    customControlData: ICustomControlDataOsasq,
    errors: ValidationErrors
) {
    if (name === 'resend_date') {
        return osasqV1RequestValidateCustomControlData(customControlData, errors);
    }

    return errors;
}

export function osasqV1RequestValidateCustomControlData(
    customControlData: ICustomControlDataOsasq,
    errors: ValidationErrors
) {
    let validationErrors = errors;

    if (
        typeof customControlData.resend_date === 'string' &&
        DateTime.from(customControlData.resend_date).isBefore(DateTime.now(), 'day')
    ) {
        validationErrors = validationErrors.set('customControlData.resend_date', 'date_past');
    }

    return validationErrors;
}

export function osasqV1RequestInitializeCustomControlData() {
    return {
        email_address: null,
        resend_date: null,
    };
}
